/**
 * Bedden.com: maat, nachtkastjes en montage (blocks/bd-addons.liquid).
 *
 * - Houdt de keuze bij en zoekt de bijbehorende variant van het optieproduct.
 * - Matras (Firm per matras) en topper zijn losse producten met een variant per afmeting; de Firm-matras
 *   in de halve breedte, de topper in de hele maat en als splittopper bij elektrische uitvoeringen.
 * - Montage wordt verplicht als de gekozen uitvoering het ingestelde woord bevat (bijv. "Elektrisch").
 * - Toont de totaalprijs in het blok en in de sticky winkelwagenbalk.
 * - Zodra de formulierdata wordt uitgelezen voor de winkelwagen (formdata-event, ook via de sticky balk)
 *   voegt het de keuzes toe als regel-eigenschappen en elke variant met meerprijs als gekoppelde regel
 *   (parent_id), zodat Horizon die genest onder de boxspring toont.
 * - Toevoegen tijdens een variantwissel wacht tot de wissel klaar is (zie #onSubmit).
 */
import { StandardEvents } from '@shopify/events';

/**
 * @param {number} cents
 * @param {string} format - shop.money_format, bijv. "€ {{amount_with_comma_separator}}"
 */
function formatMoney(cents, format) {
  const whole = Math.round(cents) % 100 === 0;
  const fmt = format || '€ {{amount_with_comma_separator}}';
  const amount = (cents / 100).toFixed(2);
  const [euros, decimals] = amount.split('.');
  const withDots = euros.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const withCommas = euros.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  const value = fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount':
        return whole ? withCommas : `${withCommas}.${decimals}`;
      case 'amount_no_decimals':
        return withCommas;
      case 'amount_no_decimals_with_comma_separator':
        return withDots;
      case 'amount_with_apostrophe_separator':
        return `${euros.replace(/\B(?=(\d{3})+(?!\d))/g, "'")}.${decimals}`;
      case 'amount_with_comma_separator':
      default:
        return whole ? withDots : `${withDots},${decimals}`;
    }
  });

  return whole ? `${value},-` : value;
}

class BdAddons extends HTMLElement {
  /** @type {{id:number, price:number, available:boolean, options:string[]}[]} */
  #addonVariants = [];
  /** Matras- en topperproducten per sleutel (data-extra-product). @type {Record<string, {label:string, variants:{id:number, price:number, available:boolean, options:string[]}[]}>} */
  #extraProducts = {};
  /** @type {Map<string, {id:number, price:number, title:string}>} */
  #productVariants = new Map();
  /** @type {{id:number, price:number, title:string} | null} */
  #mainVariant = null;
  #montageForced = false;
  #ref = '';
  /** @type {MutationObserver | null} */
  #stickyObserver = null;
  #section = /** @type {HTMLElement | null} */ (null);
  /** Lopende variantwissel van Horizon. @type {Promise<void> | null} */
  #variantChange = null;

  connectedCallback() {
    this.#section = this.closest('.shopify-section');
    this.#addonVariants = this.#readJson('[data-addon-variants]') || [];
    this.#extraProducts = this.#readJson('[data-extra-products]') || {};
    for (const variant of this.#readJson('[data-product-variants]') || []) {
      this.#productVariants.set(String(variant.id), variant);
    }
    this.#ref = `bd${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

    this.#mainVariant = this.#readCurrentVariant();

    this.addEventListener('change', this.#onChange);
    this.#section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
    document.addEventListener('formdata', this.#onFormData);
    window.addEventListener('submit', this.#onSubmit, true);

    this.#observeSticky();
    this.#update();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
    document.removeEventListener('formdata', this.#onFormData);
    window.removeEventListener('submit', this.#onSubmit, true);
    this.#stickyObserver?.disconnect();
  }

  /** @param {string} selector */
  #readJson(selector) {
    const el = this.querySelector(selector);
    if (!el?.textContent) return null;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  }

  /** Huidige hoofdvariant uit de variantkiezer of het productformulier. */
  #readCurrentVariant() {
    const pickerJson = this.#section?.querySelector('variant-picker script[type="application/json"]');
    if (pickerJson?.textContent) {
      try {
        const variant = JSON.parse(pickerJson.textContent);
        if (variant?.id) return variant;
      } catch {
        /* val terug op het formulier */
      }
    }
    const id = this.#getForm()?.querySelector('input[name="id"]')?.value;
    return (id && this.#productVariants.get(String(id))) || null;
  }

  #getForm() {
    return /** @type {HTMLFormElement | null} */ (
      this.#section?.querySelector('form[data-type="add-to-cart-form"]') ?? null
    );
  }

  /** @param {Event} event */
  #onProductSelect = (event) => {
    const promise = /** @type {any} */ (event).promise;
    if (!promise?.then) return;
    /** @type {Promise<void>} */
    const pending = promise
      .then((/** @type {any} */ result) => {
        const variant = result?.detail?.resource;
        if (variant?.id) {
          this.#mainVariant = variant;
        } else {
          this.#mainVariant = this.#readCurrentVariant();
        }
        this.#update();
      })
      .catch(() => {})
      .finally(() => {
        if (this.#variantChange === pending) this.#variantChange = null;
      });
    this.#variantChange = pending;
  };

  /**
   * Klikt iemand op toevoegen terwijl Horizon nog een variantwissel laadt, dan zet Horizon dat in een
   * wachtrij en verstuurt het later als kale JSON, zonder formulierdata: dus zonder keuzes en meerprijs.
   * Daarom houden we die submit hier tegen (window, capture: vóór Horizon op document) en versturen we
   * hem opnieuw zodra de wissel klaar is.
   * @param {SubmitEvent} event
   */
  #onSubmit = (event) => {
    const form = this.#getForm();
    if (!form || event.target !== form || !this.#variantChange) return;

    event.preventDefault();
    event.stopPropagation();
    this.#submitAfterVariantChange();
  };

  async #submitAfterVariantChange() {
    // Wacht op de laatste wissel, maar nooit langer dan 8 seconden (anders reageert de knop niet meer)
    const deadline = Date.now() + 8000;
    while (this.#variantChange && Date.now() < deadline) {
      await Promise.race([this.#variantChange, new Promise((resolve) => setTimeout(resolve, 1000))]);
    }
    this.#variantChange = null;
    // Horizon zet zijn eigen wachtvlag in dezelfde ronde terug; in een nieuwe taak is die zeker weg
    await new Promise((resolve) => setTimeout(resolve));
    this.#getForm()?.requestSubmit();
  }

  #onChange = () => {
    // Montage handmatig aangezet: niet meer automatisch terugzetten
    const montage = this.querySelector('[data-montage]');
    if (montage && !this.#isMontageRequired()) this.#montageForced = false;
    this.#update();
  };

  /** Alle gekozen waarden in de volgorde op de pagina (ook matras en topper), voor de regel-eigenschappen. */
  #getSelection() {
    return [...this.querySelectorAll('fieldset.bd-addons__group')].map((fieldset) => {
      const input = /** @type {HTMLInputElement | null} */ (fieldset.querySelector('input:checked'));
      return {
        name: fieldset.querySelector('.bd-addons__legend span')?.textContent?.trim() || '',
        value: input?.value || '',
        label: input?.dataset.label || input?.value || '',
      };
    });
  }

  #findAddonVariant() {
    const values = [...this.querySelectorAll('fieldset[data-option-index]')].map(
      (fieldset) => /** @type {HTMLInputElement | null} */ (fieldset.querySelector('input:checked'))?.value || ''
    );
    return this.#addonVariants.find((variant) => variant.options.every((value, index) => value === values[index])) || null;
  }

  /** Gekozen afmeting als "160*200cm", en de maat van één matras daarbij ("80*200cm"). */
  #getSizes() {
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('.bd-addons__group--size input:checked'));
    const full = (input?.value || '').split(' (')[0].trim();
    const match = full.match(/^(\d+)\s*\*\s*(\d+)/);
    const half = match ? `${Math.floor(Number(match[1]) / 2)}*${match[2]}cm` : '';
    return { full, half };
  }

  /**
   * Variant van het matras- of topperproduct bij de gekozen maat. Eerste optie is de afmeting, een tweede
   * optie (Splittopper) is "Ja" bij de uitvoeringen waar montage verplicht is (elektrisch), anders "Nee".
   * @param {HTMLInputElement} input
   */
  #findExtraVariant(input) {
    const product = this.#extraProducts[input.dataset.extraProduct || ''];
    if (!product) return null;
    const sizes = this.#getSizes();
    const size = input.dataset.extraSize === 'half' ? sizes.half : sizes.full;
    const split = this.#isMontageRequired() ? 'ja' : 'nee';
    return (
      product.variants.find(
        (variant) =>
          variant.options[0] === size && (variant.options.length < 2 || variant.options[1].toLowerCase() === split)
      ) || null
    );
  }

  /** Gekozen matras en topper met meerprijs, met het aantal per boxspring. */
  #getExtras() {
    const extras = [];
    for (const input of /** @type {NodeListOf<HTMLInputElement>} */ (
      this.querySelectorAll('fieldset[data-extra] input:checked')
    )) {
      const product = this.#extraProducts[input.dataset.extraProduct || ''];
      const variant = product && this.#findExtraVariant(input);
      if (!variant?.available) continue;
      extras.push({ variant, quantity: Number(input.dataset.extraQuantity) || 1, label: product.label });
    }
    return extras;
  }

  /** Meerprijs per keuze bij de huidige maat tonen, en keuzes zonder leverbare variant uitzetten. */
  #updateExtras() {
    const format = this.dataset.moneyFormat || '';
    for (const fieldset of this.querySelectorAll('fieldset[data-extra]')) {
      const inputs = /** @type {HTMLInputElement[]} */ ([...fieldset.querySelectorAll('input')]);
      for (const input of inputs) {
        if (!input.dataset.extraProduct) continue;
        const variant = this.#findExtraVariant(input);
        input.disabled = !variant?.available;
        const price = input.closest('label')?.querySelector('[data-extra-price]');
        if (price && variant) {
          price.textContent = `+ ${formatMoney(variant.price * (Number(input.dataset.extraQuantity) || 1), format)}`;
        }
      }
      // Niet leverbaar in deze maat: terug naar de standaardkeuze
      const first = inputs[0];
      if (first && fieldset.querySelector('input:checked:disabled')) first.checked = true;
    }
  }

  #isMontageRequired() {
    const keyword = (this.dataset.requiredKeyword || '').toLowerCase().trim();
    if (!keyword || !this.#mainVariant) return false;
    return String(this.#mainVariant.title || '').toLowerCase().includes(keyword);
  }

  /** Montage verplicht maken of weer vrijgeven. */
  #applyMontageRule() {
    const fieldset = this.querySelector('[data-montage]');
    if (!fieldset) return;
    const inputs = /** @type {HTMLInputElement[]} */ ([...fieldset.querySelectorAll('input')]);
    if (inputs.length < 2) return;
    const [noMontage, ...withMontage] = inputs;
    const note = fieldset.querySelector('[data-montage-note]');

    if (this.#isMontageRequired()) {
      if (noMontage.checked) {
        withMontage[0].checked = true;
        this.#montageForced = true;
      }
      noMontage.disabled = true;
      if (note) note.hidden = false;
    } else {
      noMontage.disabled = false;
      if (note) note.hidden = true;
      if (this.#montageForced) {
        noMontage.checked = true;
        this.#montageForced = false;
      }
    }
  }

  #getTotals() {
    const base = Number(this.#mainVariant?.price) || 0;
    const addon = this.#findAddonVariant();
    const extras = this.#getExtras();
    const extra =
      (addon ? Number(addon.price) || 0 : 0) +
      extras.reduce((sum, { variant, quantity }) => sum + (Number(variant.price) || 0) * quantity, 0);
    return { base, extra, total: base + extra, addon, extras };
  }

  #update() {
    this.#applyMontageRule();
    this.#updateExtras();

    for (const fieldset of this.querySelectorAll('fieldset.bd-addons__group')) {
      const input = /** @type {HTMLInputElement | null} */ (fieldset.querySelector('input:checked'));
      const label = fieldset.querySelector('[data-selected-label]');
      if (label) label.textContent = input?.dataset.label || '';
    }

    const format = this.dataset.moneyFormat || '';
    const { base, extra, total } = this.#getTotals();

    const baseLabel = this.querySelector('[data-summary-base-label]');
    if (baseLabel && this.#mainVariant?.title) {
      baseLabel.textContent = this.#mainVariant.title.replace(/ \/ /g, ', ');
    }
    const basePrice = this.querySelector('[data-summary-base-price]');
    if (basePrice) basePrice.textContent = formatMoney(base, format);

    const extraLine = /** @type {HTMLElement | null} */ (this.querySelector('[data-summary-extra]'));
    if (extraLine) extraLine.hidden = extra <= 0;
    const extraPrice = this.querySelector('[data-summary-extra-price]');
    if (extraPrice) extraPrice.textContent = `+ ${formatMoney(extra, format)}`;

    const totalEl = this.querySelector('[data-summary-total]');
    if (totalEl) totalEl.textContent = formatMoney(total, format);

    this.#renderStickyTotal();
  }

  /** De sticky balk toont standaard alleen de prijs van de boxspring; vervang die door het totaal. */
  #renderStickyTotal() {
    const target = this.#section?.querySelector('.sticky-add-to-cart__price');
    if (!target || !this.#mainVariant) return;
    const { total } = this.#getTotals();
    const text = formatMoney(total, this.dataset.moneyFormat || '');
    const bdi = target.querySelector('.price bdi, bdi');
    if (bdi) {
      if (bdi.textContent !== text) bdi.textContent = text;
    } else if (target.textContent?.trim() !== text) {
      target.textContent = text;
    }
  }

  #observeSticky() {
    const target = this.#section?.querySelector('.sticky-add-to-cart__price');
    if (!target) return;
    this.#stickyObserver = new MutationObserver(() => this.#renderStickyTotal());
    this.#stickyObserver.observe(target, { childList: true, subtree: true, characterData: true });
  }

  /**
   * Voegt de keuzes toe aan de formulierdata van het productformulier. Horizon leest die bij toevoegen
   * uit met new FormData(form), wat dit event afvuurt. Niet via submit: Horizon luistert daar zelf al
   * in de capture-fase op document en verstuurt het verzoek voordat een latere listener aan de beurt is.
   * @param {FormDataEvent} event
   */
  #onFormData = (event) => {
    const form = this.#getForm();
    if (!form || event.target !== form) return;

    this.#applyMontageRule();
    this.#updateExtras();
    const { formData } = event;
    const selection = this.#getSelection();
    const { addon, extras } = this.#getTotals();
    const parentId = String(formData.get('id') || '');
    const quantity = Number(formData.get('quantity')) || 1;
    const ref = `${this.#ref}${Date.now().toString(36)}`;

    for (const { name, label } of selection) {
      if (name && label) formData.set(`properties[${name}]`, label);
    }
    formData.set('properties[_bd_ref]', ref);
    if (!parentId) return;

    // Elke meerprijs als eigen regel onder de boxspring; _bd_addon is ook de naam in de winkelwagen
    let index = 1;
    /** @param {number} id @param {number} childQuantity @param {string} label */
    const addChild = (id, childQuantity, label) => {
      formData.set(`items[${index}][id]`, String(id));
      formData.set(`items[${index}][quantity]`, String(childQuantity));
      formData.set(`items[${index}][parent_id]`, parentId);
      formData.set(`items[${index}][properties][_bd_ref]`, ref);
      formData.set(`items[${index}][properties][_bd_addon]`, label);
      index += 1;
    };
    if (addon && addon.price > 0) addChild(addon.id, quantity, "Maat en extra's");
    for (const { variant, quantity: perBed, label } of extras) {
      if (variant.price > 0) addChild(variant.id, perBed * quantity, label);
    }
  };
}

if (!customElements.get('bd-addons')) {
  customElements.define('bd-addons', BdAddons);
}
